/* eslint-disable complexity,@typescript-eslint/no-unsafe-argument,@typescript-eslint/ban-ts-comment */
/* eslint-disable max-statements */
/* eslint-disable camelcase */
import authApiService from './auth.api-service';
import { RootState } from '../../redux/redux-store';
import axios from 'axios';
import ErrorLogService from '../error-log/error-log.service';
import ReduxService from '../../redux/redux.service';
import AsyncStorageService, { AsyncStorageKeys } from '../storage/async-storage.service';
import { v4 as uuid } from 'uuid';
import { asyncLogger } from '../error-log/decorators/async-logger.decorator';
import { clearUserState } from '../../redux/user/user.slice';
import { ISignInRequest, ISignUpRequest, ISocialLoginRequest } from './auth.types';
import { User } from '../user/user.types';

import { ServerResponseEnum } from '../api/invisibly-api.constants';

interface IAuthService {
  readonly checkDeviceIdLinked: (deviceId: string) => Promise<boolean>;
  readonly createNewAnonymousUser: (deviceId: string) => Promise<User | null>;
  readonly forgotPassword: (email: string) => Promise<void>;
  readonly initUserAuthInvalidInterceptor: (callBackAction: () => void) => void;
  readonly linkDeviceIdToUser: (userId: string) => Promise<void>;
  readonly resetPassword: (password: string, token: string) => Promise<string | null>;
  readonly signIn: (email: string, password: string) => Promise<{user: User, access: string, refresh: string}>;
  readonly signInWithGoogle: (idToken: string) => Promise<{user: User, access: string, refresh: string}>;
  readonly signInWithApple: (idToken: string) => Promise<{user: User, access: string, refresh: string}>;
  readonly signUp: (email: string, password: string, state: RootState) => Promise<{user: User, access: string, refresh: string}>;
}

class AuthService implements IAuthService {
  private static instance: AuthService | undefined;
  private static hasInitAuthInvalidInterceptor = false;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }

    return AuthService.instance;
  }

  private getQueryParam(param: string): string | null {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get(param);
  }

  public initUserAuthInvalidInterceptor(callBackAction: () => void): void {
    if (AuthService.hasInitAuthInvalidInterceptor) {
      return;
    }

    AuthService.hasInitAuthInvalidInterceptor = true;
    axios.interceptors.response.use(
      response => {
        const code = response.data?.status?.status_code ?? '';
        const tokenMissingOrExpired = code === ServerResponseEnum.AUTH_NOT_PROVIDED;
        const tokenPermissionError = code === ServerResponseEnum.TOKEN_PERMISSION_ERROR;
        if (tokenMissingOrExpired || tokenPermissionError) {
          ErrorLogService.logError(new Error('Authentication not provided'), 'app', 'interceptor');
          // TODO: re add and dispatch action instead
          const { userState } = ReduxService.Store.getState();

          // if (!userState.anonymousUser && !userState.signOutInProgress) {
          //   const callbackToken = tokenPermissionError ? '' : userState.jwt;
          //   this.handleInterceptorRejection(callBackAction, callbackToken);
          // }
        }

        return response;
      }
    );
  }

  private handleInterceptorRejection(callBackAction: () => void, token: string): void {
    if (token) {
      callBackAction();
    } else {
      ReduxService.Store.dispatch(clearUserState());
    }
  }

  public async checkDeviceIdLinked(deviceId: string): Promise<boolean> {
    const { isAnonymous, userExists } = await authApiService.checkDeviceTokenAccountExists(deviceId);

    return userExists && !isAnonymous;
  }

  public async createNewAnonymousUser(deviceId: string): Promise<User | null> {
    return await authApiService.createAnonymousUser(deviceId);
  }

  @asyncLogger()
  public async linkDeviceIdToUser(userId: string): Promise<void> {
    // eslint-disable-next-line prefer-const
    let [deviceId, deviceUserIdsLinked] = await AsyncStorageService.localStorage.retrieveMultiData(
      [AsyncStorageKeys.deviceId, AsyncStorageKeys.deviceUserIdLinked]
    );

    if (!deviceId) {
      deviceId = uuid();
      await AsyncStorageService.localStorage.storeData(AsyncStorageKeys.deviceId, deviceId);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    if (deviceUserIdsLinked?.includes(userId)) {
      return;
    }

    await authApiService.linkDeviceIdToUserId(deviceId);

    const data = deviceUserIdsLinked ? [...deviceUserIdsLinked, userId] : [userId];

    await AsyncStorageService.localStorage.storeData(AsyncStorageKeys.deviceUserIdLinked, data);
  }

  public async requestSignInWithUuid(email: string): Promise<{message: string}> {
    const details: ISignInRequest = {
      email: email,
      login_token: email,
      password: email,
      platform: 'web',
      service: 'web',
      user_access_type: 'b2b',
      invite_code: this.getQueryParam('invite_code') ?? undefined,
      registration_type: (['b2b', 'client', 'organization'].includes(this.getQueryParam('registration_type') ?? '')
        ? this.getQueryParam('registration_type')
        : undefined) as 'b2b' | 'client' | 'organization' | undefined,
    };

    return await authApiService.requestSignInWithUuid(details);
  }

  public async signInWithUuid(uuid: string): Promise<{user: User, access: string, refresh: string}> {
    const details: ISignInRequest = {
      email: uuid,
      token: uuid,
      platform: 'web',
      service: 'web_email_only',
      user_access_type: 'b2b',
      password: uuid,
    };

    return await authApiService.signInWithUuid(details);
  }

  public async signIn(email: string, password: string): Promise<{user: User, access: string, refresh: string}> {
    const deviceId = await AsyncStorageService.localStorage.retrieveData<string>(AsyncStorageKeys.deviceId);
    const details: ISignInRequest = {
      device_id: deviceId ?? '',
      email,
      password,
      platform: 'web',
      service: 'web',
      user_access_type: 'b2b',
    };

    return await authApiService.signIn(details);
  }

  public async signInWithGoogle(idToken: string): Promise<{user: User, access: string, refresh: string}> {
    const deviceId = await AsyncStorageService.localStorage.retrieveData<string>(AsyncStorageKeys.deviceId);
    // @ts-expect-error not implementing all fields
    const details: ISocialLoginRequest = {
      device_id: deviceId ?? '',
      platform: 'web',
      service: 'google',
      token: idToken,
      user_access_type: 'b2b',
      invite_code: this.getQueryParam('invite_code') ?? undefined,
      registration_type: (['b2b', 'client', 'organization'].includes(this.getQueryParam('registration_type') ?? '')
        ? this.getQueryParam('registration_type')
        : undefined) as 'b2b' | 'client' | 'organization' | undefined,
    };

    return await authApiService.socialLogin(details);
  }

  public async signInWithApple(idToken: string): Promise<{user: User, access: string, refresh: string}> {
    const deviceId = await AsyncStorageService.localStorage.retrieveData<string>(AsyncStorageKeys.deviceId);
    // @ts-expect-error not implementing all fields
    const details: ISocialLoginRequest = {
      device_id: deviceId ?? '',
      platform: 'web',
      service: 'apple',
      token: idToken,
      user_access_type: 'b2b',
      invite_code: this.getQueryParam('invite_code') ?? undefined,
      registration_type: (['b2b', 'client', 'organization'].includes(this.getQueryParam('registration_type') ?? '')
        ? this.getQueryParam('registration_type')
        : undefined) as 'b2b' | 'client' | 'organization' | undefined,
    };
    return await authApiService.socialLogin(details);
  }

  public async signUp(email: string, password: string, state: RootState): Promise<{user: User, access: string, refresh: string}> {
    const deviceId = await AsyncStorageService.localStorage.retrieveData<string>(AsyncStorageKeys.deviceId);
    const details: ISignUpRequest = {
      consent: true,
      email,
      password,
      platform: 'web',
      plugin_install: 0,
      service: 'web',
      user_access_type: 'b2b',
      invite_code: this.getQueryParam('invite_code') ?? undefined,
      registration_type: (['b2b', 'client', 'organization'].includes(this.getQueryParam('registration_type') ?? '')
        ? this.getQueryParam('registration_type')
        : undefined) as 'b2b' | 'client' | 'organization' | undefined,
    };

    // if (deviceId) {
    //   const isDeviceLinked = await this.checkDeviceIdLinked(deviceId);

    //   if (!isDeviceLinked) {
    //     details.device_id = fernetEncrypt(deviceId);
    //     details.anonymous_user_signup = state.userState.anonymousUser;
    //   }
    // }

    const userResponse = await authApiService.signUp(details);

    return userResponse;
  }

  public async forgotPassword(email: string): Promise<void> {
    await authApiService.forgotPassword(email);
  }

  public async resetPassword(password: string, token: string): Promise<string | null> {
    return await authApiService.resetPassword(password, token);
  }

  public async signOut(): Promise<void> {
    const { userState: { refresh } } = ReduxService.Store.getState();
    await authApiService.signOut(refresh);
  }
}

export default AuthService.getInstance();
